import { useCallback, useState } from 'react'
import {
  InventoryScanError,
  ResourceMismatchError,
  type StockScanDirection,
  type StockScanTargetType,
} from '../services/InventoryScannerService'
import {
  findStockItemForBarcode,
  buildScanSuggestion,
  type StockCountActorRole,
} from '../services/stockCountSuggestionService'
import { queueStockScanSuggestion } from '../services/helper/queueService'
import {
  postStockMovements,
  StockLedgerClientError,
} from '../services/stock/stockLedgerService'
import type { StockItem } from '../types/stock'

export interface StockScanActor {
  id: string
  name: string
  role?: StockCountActorRole
}

export interface UseStockScanOptions {
  workspaceId: string | null
  actor: StockScanActor | null
  mode: 'helper' | 'member'
}

export interface StockScanTargetContext {
  taskId?: string | null
  checklistItemId?: string | null
  targetType?: StockScanTargetType | string | null
  targetId?: string | null
  targetTitle?: string | null
  requiredSku?: string | null
  requiredItemName?: string | null
}

export interface StockScanResult {
  item: StockItem
  action: StockScanDirection
  quantityDelta: number
  checklistItemId?: string | null
  suggestionId?: string
  movementIds?: string[]
}

export const useStockScan = (options: UseStockScanOptions) => {
  const { workspaceId, actor, mode } = options
  const [scanning, setScanning] = useState(false)
  const [lastError, setLastError] = useState<Error | null>(null)

  const scanStock = useCallback(
    async (
      barcode: string,
      direction: StockScanDirection = 'OUT',
      context?: StockScanTargetContext,
    ): Promise<StockScanResult> => {
      const code = barcode.trim()
      if (!code) {
        throw new InventoryScanError('INVENTORY_NOT_FOUND', 'Scan did not include a barcode.')
      }
      if (!workspaceId) {
        throw new Error('Workspace ID is required for stock scan.')
      }
      if (!actor) {
        throw new Error('Actor is required for stock scan.')
      }

      setScanning(true)
      setLastError(null)

      try {
        const item = await findStockItemForBarcode(workspaceId, code)
        if (!item) {
          throw new InventoryScanError('INVENTORY_NOT_FOUND', `No inventory item found for ${code}.`)
        }

        if (context?.requiredSku) {
          const itemIdentifier = item.sku || item.id
          if (context.requiredSku !== itemIdentifier && context.requiredSku !== item.id && context.requiredSku !== item.sku) {
            throw new ResourceMismatchError(
              item.name,
              context.targetTitle || 'this task',
              context.requiredItemName || context.requiredSku,
            )
          }
        }

        if (mode === 'helper') {
          const payload = buildScanSuggestion({
            workspaceId,
            actor: {
              id: actor.id,
              name: actor.name,
              role: actor.role ?? 'HELPER',
            },
            stockItemId: item.id,
            sku: item.sku,
            barcode: code,
            direction,
            quantity: 1,
            systemQuantityAtScan: item.quantity,
            taskId: context?.taskId ?? null,
            locationId: null,
            note: null,
            timestamp: Date.now(),
          })

          const record = await queueStockScanSuggestion(payload)
          const suggestionId = record.id

          return {
            item,
            action: direction,
            quantityDelta: direction === 'IN' ? 1 : -1,
            checklistItemId: context?.checklistItemId ?? null,
            suggestionId,
          }
        }

        // Member mode: authoritative direct movement
        if (direction === 'OUT' && item.quantity < 1) {
          throw new InventoryScanError('INSUFFICIENT_STOCK', `Insufficient Stock: ${item.name}.`)
        }

        try {
          const postResult = await postStockMovements(workspaceId, [
            {
              itemId: item.id,
              type: direction,
              quantity: 1,
              source: { kind: 'MANUAL', id: `scan-${Date.now()}` },
              reason: context?.targetTitle ? `Scan for ${context.targetTitle}` : undefined,
            },
          ])

          return {
            item,
            action: direction,
            quantityDelta: direction === 'IN' ? 1 : -1,
            checklistItemId: context?.checklistItemId ?? null,
            movementIds: postResult.movementIds,
          }
        } catch (postErr) {
          if (postErr instanceof StockLedgerClientError && postErr.code === 'STOCK_INSUFFICIENT') {
            throw new InventoryScanError('INSUFFICIENT_STOCK', `Insufficient Stock: ${item.name}.`)
          }
          if (postErr instanceof StockLedgerClientError && postErr.code === 'ITEM_NOT_FOUND') {
            throw new InventoryScanError('INVENTORY_NOT_FOUND', `No inventory item found for ${code}.`)
          }
          throw postErr
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        setLastError(error)
        throw error
      } finally {
        setScanning(false)
      }
    },
    [actor, mode, workspaceId],
  )

  return {
    scanStock,
    scanning,
    lastError,
  }
}
